class ProductCardSwatches extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.list = this.querySelector('.product-card-swatches__list');
    this.cardWrapper = this.closest('.product-card-wrapper');
    this.primaryImage = this.cardWrapper?.querySelector('[data-product-card-primary-image]');
    this.priceElement = this.cardWrapper?.querySelector('.card-information > .price');
    this.previewToken = 0;

    this.addEventListener('pointerover', this.previewSwatch.bind(this), { signal: this.abortController.signal });
    this.addEventListener('focusin', this.previewSwatch.bind(this), { signal: this.abortController.signal });
    this.addEventListener('pointerleave', this.restoreSwatch.bind(this), { signal: this.abortController.signal });
    this.addEventListener('focusout', this.handleFocusOut.bind(this), { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  previewSwatch(event) {
    const swatch = event.target.closest?.('.product-card-swatch');
    if (!swatch || !this.contains(swatch)) return;

    this.previewImage(swatch);
    this.previewPrice(swatch);
  }

  previewImage(swatch) {
    if (!this.primaryImage) return;

    if (!swatch.dataset.cardImageSrc) {
      this.restoreImage();
      return;
    }

    if (!this.primaryImage.dataset.originalSrc) {
      this.primaryImage.dataset.originalSrc =
        this.primaryImage.dataset.originalSrc || this.primaryImage.getAttribute('src') || this.primaryImage.src;
      this.primaryImage.dataset.originalSrcset =
        this.primaryImage.dataset.originalSrcset || this.primaryImage.getAttribute('srcset') || '';
    }

    const nextSrc = swatch.dataset.cardImageSrc;
    const nextSrcset = swatch.dataset.cardImageSrcset || '';
    const currentSrc = this.primaryImage.getAttribute('src');
    const currentSrcset = this.primaryImage.getAttribute('srcset') || '';

    if (currentSrc === nextSrc && currentSrcset === nextSrcset) {
      this.cardWrapper?.classList.add('product-card-wrapper--swatch-preview');
      return;
    }

    const token = ++this.previewToken;
    const preview = new Image();
    if (nextSrcset) preview.srcset = nextSrcset;
    preview.sizes = this.primaryImage.sizes;
    preview.src = nextSrc;

    const applyPreview = () => {
      if (token !== this.previewToken || !this.isConnected) return;

      this.cardWrapper?.classList.add('product-card-wrapper--swatch-preview');
      this.setPrimaryImageSource(nextSrc, nextSrcset);
    };

    if (preview.decode) {
      preview.decode().then(applyPreview).catch(applyPreview);
    } else {
      preview.onload = applyPreview;
      preview.onerror = applyPreview;
    }
  }

  previewPrice(swatch) {
    if (!this.priceElement || !swatch.dataset.cardPrice) return;

    if (this.originalPriceHTML === undefined) {
      this.originalPriceHTML = this.priceElement.innerHTML;
      this.originalPriceClass = this.priceElement.className;
    }

    const price = swatch.dataset.cardPrice;
    const comparePrice = swatch.dataset.cardComparePrice;
    const discount = swatch.dataset.cardDiscount;
    const hasDiscount = Boolean(comparePrice);

    this.priceElement.className = this.originalPriceClass;
    this.priceElement.classList.toggle('price--on-sale', hasDiscount);

    if (hasDiscount) {
      this.priceElement.innerHTML = `
        <div class="price__container">
          <div class="price__regular">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="price-item price-item--regular">${this.escapeHTML(price)}</span>
          </div>
          <div class="price__sale">
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="price-item price-item--sale price-item--last">${this.escapeHTML(price)}</span>
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span>
              <s class="price-item price-item--regular price-item--compare">${this.escapeHTML(comparePrice)}</s>
            </span>
            ${discount ? `<span class="price-item price-item--discount">(${this.escapeHTML(discount)}% off)</span>` : ''}
          </div>
        </div>
      `;
      return;
    }

    this.priceElement.innerHTML = `
      <div class="price__container">
        <div class="price__regular">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="price-item price-item--regular">${this.escapeHTML(price)}</span>
        </div>
        <div class="price__sale">
          <span class="visually-hidden visually-hidden--inline">Sale price</span>
          <span class="price-item price-item--sale price-item--last">${this.escapeHTML(price)}</span>
        </div>
      </div>
    `;
  }

  restoreImage() {
    if (!this.primaryImage) return;

    this.previewToken += 1;
    this.cardWrapper?.classList.remove('product-card-wrapper--swatch-preview');
    if (this.primaryImage.dataset.originalSrc) {
      this.setPrimaryImageSource(this.primaryImage.dataset.originalSrc, this.primaryImage.dataset.originalSrcset || '');
    }
  }

  restorePrice() {
    if (!this.priceElement || this.originalPriceHTML === undefined) return;

    this.priceElement.className = this.originalPriceClass;
    this.priceElement.innerHTML = this.originalPriceHTML;
  }

  restoreSwatch() {
    this.restoreImage();
    this.restorePrice();
  }

  handleFocusOut(event) {
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    this.restoreSwatch();
  }

  setPrimaryImageSource(src, srcset) {
    if (this.primaryImage.getAttribute('src') !== src) this.primaryImage.setAttribute('src', src);

    if (srcset) {
      if ((this.primaryImage.getAttribute('srcset') || '') !== srcset) this.primaryImage.setAttribute('srcset', srcset);
      return;
    }

    if (this.primaryImage.hasAttribute('srcset')) this.primaryImage.removeAttribute('srcset');
  }

  escapeHTML(value) {
    return String(value).replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };

      return entities[character];
    });
  }
}

if (!customElements.get('product-card-swatches')) {
  customElements.define('product-card-swatches', ProductCardSwatches);
}
